import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@antimatterhq/ui/components/button';
import Table from '@antimatterhq/ui/components/table';
import { core_v1_Transform } from '@antimatterhq/asl-api-ts-v1';
import { useTableColumnFiltering } from '@antimatterhq/ui/hooks/table/useTableColumnFiltering';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { useTableColumnSorting } from '@antimatterhq/ui/hooks/table/useTableColumnSorting';
import { useTableRowFuzzyFiltering } from '@antimatterhq/ui/hooks/table/useTableRowFuzzyFiltering';
import { Input } from '@antimatterhq/ui/components/input';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useVirtuosoTableRows } from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import Spacer from '@antimatterhq/ui/components/spacer';
import { transformTableColumnDefinitions } from '@/components/table/TransformTable/transformTableColumnDefinitions';
import { TransformTableData } from '@/components/table/TransformTable/TransformTableData';
import { cn } from '@/lib/utils';
import { DeleteTransformDialog } from '@/components/transform/deleteTransformDialog/DeleteTransformDialog';
import { useGetTransformsInfinite } from '@/hooks/core/useGetTransformsInfinite';

const transformTransformData = (
  transform: core_v1_Transform,
): TransformTableData => ({
  id: transform.metadata?.name ?? '',
  name: transform.metadata?.name ?? '',
  comment: transform.metadata?.comment ?? '',
  lastModified: transform.metadata?.modifiedTimestamp
    ? new Date(transform.metadata?.modifiedTimestamp)
    : undefined,
  annotations: Object.entries(transform.metadata?.annotations ?? {})
    .map(([key, value]) => `${key}: ${value}`)
    .join(' | '),
  inputTables: (
    (transform.spec.stream?.tables
      ?.map((table) => table.name)
      .filter(Boolean) as string[]) ?? []
  ).join(', '),
  schedule:
    transform.spec.schedule?.atLeastEvery ??
    transform.spec.schedule?.exactly ??
    'continuous',
  originalTransform: transform,
});

// TODO: Not sure if we want to prefetch all like detection rules or not
const useGetAllTransforms = () => {
  const {
    data,
    isLoading: isLoadingTransforms,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    refetch,
  } = useGetTransformsInfinite();

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const isLoading = isLoadingTransforms || isFetchingNextPage || hasNextPage;
  const items = useMemo(
    () =>
      data?.pages.flatMap((page) => page.items.map(transformTransformData)) ??
      [],
    [data?.pages],
  );

  return { items, isLoading, error, refetch };
};

function TransformTableContent() {
  const navigate = useNavigate();
  const [selectedTransform, setSelectedTransform] = useState<
    core_v1_Transform | undefined
  >(undefined);

  const { items, isLoading, error, refetch } = useGetAllTransforms();

  // Filters on specific columns
  const { filteredItems, setFilter, filters } = useTableColumnFiltering(items);

  // Sorts the items for specific columns
  const { sortedItems, setSortBy, sortKey, sortDirection } =
    useTableColumnSorting(filteredItems);

  // Creates & manages the columns
  const {
    columns,
    columnVisibility,
    columnOrders,
    columnWidths,
    pinnedColumns,
    toggleColumnVisibility,
    resetAllColumns,
    onColumnOrderChange,
  } = useTableColumns({
    sortKey,
    sortDirection,
    setSortBy,
    setFilter,
    filters,
  });

  // Searches all columns
  const { fuzzyFilter, fuzzyFilteredItems, setFuzzyFilter } =
    useTableRowFuzzyFiltering(sortedItems, columnVisibility);

  const { itemContent, fixedHeaderContent } =
    useVirtuosoTableRows<TransformTableData>({
      columns,
      customBodyCells: useMemo(
        () => ({
          edit: ({ item }) => (
            <Table.ActionsCell
              data-testid={item.id}
              actions={[
                {
                  label: 'Delete',
                  icon: 'antimatterhq:trash',
                  onClick: () => {
                    setSelectedTransform(item.originalTransform);
                  },
                },
                {
                  label: 'Edit',
                  icon: 'antimatterhq:pencil',
                  onClick: () =>
                    navigate(`/configure/transforms/edit/${item.name}`),
                },
              ]}
            />
          ),
        }),
        [navigate],
      ),
    });

  const handleCreateNewTransform = useCallback(() => {
    navigate('/configure/transforms/add');
  }, [navigate]);

  return (
    <>
      <Table.VirtualizedPageContainer>
        <div className="flex items-center justify-end gap-2">
          <div className="relative mr-auto flex w-64">
            <Input
              placeholder="Filter all columns"
              value={fuzzyFilter}
              onChange={(e) => setFuzzyFilter(e.target.value)}
              className={cn('mr-1 w-64', !fuzzyFilter && 'mr-auto')}
            />
            {fuzzyFilter && (
              <Button
                variant="tertiary"
                className="absolute right-0 top-1/2 -translate-y-1/2"
                onClick={() => setFuzzyFilter('')}
              >
                <Icon icon="lucide:x" />
              </Button>
            )}
          </div>
          <Table.ColumnManager
            pinnedColumns={pinnedColumns}
            columnOrders={columnOrders}
            columnVisibility={columnVisibility}
            toggleColumnVisibility={toggleColumnVisibility}
            resetColumns={resetAllColumns}
            onColumnOrderChange={onColumnOrderChange}
            data-testid="transform-column-manager"
          />
          <Button
            variant="primary"
            onClick={handleCreateNewTransform}
            leftIcon="radix-icons:plus"
            data-testid="create-new-transform-button"
          >
            Create New Transform
          </Button>
        </div>

        <Spacer size="lg" />

        <Table.StyleProvider
          virtualized={true}
          pageSize={fuzzyFilteredItems.length}
          columnWidths={columnWidths}
          columnVisibility={columnVisibility}
          data-testid="transform-table-style-provider"
        >
          <Table.Virtual
            columns={columns}
            data={fuzzyFilteredItems}
            itemContent={itemContent}
            fixedHeaderContent={fixedHeaderContent}
            isLoading={isLoading}
            emptyMessage="No transforms found"
            error={error}
            refetch={refetch}
          />
        </Table.StyleProvider>
      </Table.VirtualizedPageContainer>
      <DeleteTransformDialog
        transform={selectedTransform}
        onClose={() => setSelectedTransform(undefined)}
      />
    </>
  );
}

export function TransformTable() {
  return (
    <Table.Provider
      columnDefinitions={transformTableColumnDefinitions}
      localStorageKeyPrefix="transform-table"
    >
      <TransformTableContent />
    </Table.Provider>
  );
}
